import { useState } from "react";
import { documentHasFileId, lienDocumentIsAttached, lienSideHasRecordedBookPage } from "../../../../lib/docs";
import { getLienStageDataFromLienRowType } from "../../../../lib/lienCreationErrors";
import { getDisplayNameForLien, getDisplayNameForLienWithCounty } from "../../../../lib/misc";
import { getPracticeIntentNoticeDocuments } from "../../../../lib/lienIntentNotices";
import { resolveSatisfactionSourceLienForDisplay, sourceLienBookPage } from "../../../../lib/lienSatisfaction";
import { formatDateLocal, formatTimeLocal } from "../../../../lib/script";
import {
    formatCreatedOnHover,
    getLienCreatedDate,
    getLienCreatedDateForSide,
    getLienDisplayDate,
    getLienDisplayDateForSide,
    lienDisplayUsesRecordingDate,
} from "../../../../lib/lienDisplayDate";
import { isTestCaseForLien } from "../../../../lib/systemData";
import { useUserTabEditPermissionDisabled } from "../../../../lib/usersPermissions";
import { recreateLien } from "../../../../lib/fetchApi";
import {
    getLienSideStatus,
    isValidGscccaPackageUuid,
    itemHasRejectedSide,
    lienNeedsManualRegenerate,
    lienNeedsPaymentRetryOnly,
    lienSideHasApiError,
    lienSideHasRetryableProblem,
    lienSideIsRejected,
    shouldShowLienRetryActions,
    sideCanFetchLienStatus,
    sideNeedsFullResubmit,
    sideNeedsManualRegenerate,
    sideNeedsPaymentRetry,
    sidesForTargetType,
} from "../../../../lib/lienRetry";
import { notificationActions } from "../../../../store/notifications";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../common/Button";
import CopyIconButton from "../../../fragments/CopyIconButton";
import DisplayBoxRight from "../../../fragments/DisplayBoxRight";
import HoverWrapperInfo from "../../../wrappers/HoverWrapperInfo";
import LienBookPage from "./LienBookPage";
import CertifiedMailNumber from "./CertifiedMailNumber";
import BodySimpleModalV3 from "../../../common/modal/BodySimpleModalV3";
import ViewDocument from "../procodocs/documents/controls/ViewDocument";
import UploadRecordedLienDocument from "./UploadRecordedLienDocument";
import FetchLienStatus from "./FetchLienStatus";
import { useLienRetryConfirm } from "./lienRetryConfirm";
import classes from "./LienDrawerItem.module.css";

export default function LienDrawerItem({
    item,
    data,
    fullData,
    activeCase,
    clinic = null,
    mode = 'full',
    documentProp = 'patient_document',
    bookProp = 'practice_book',
    pageProp = 'practice_page',
    uuidProp = null,
}) {
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const activeCaseState = useSelector(state => state.case.activeCase)
    const lienEditDisabled = useUserTabEditPermissionDisabled('lien_edit')
    useUserTabEditPermissionDisabled('lien_book_page_edit')
    const [recreateLoading, setRecreateLoading] = useState(false)
    const { confirm: confirmLienRetry, modal: lienRetryConfirmModal } = useLienRetryConfirm()

    const viewDoc = item?.[documentProp]
    const uuidValue = uuidProp ? item?.[uuidProp] : null
    const stageBlock = getLienStageDataFromLienRowType(fullData?.lien_creation_errors, item?.type)
    const stageErrorsByCategory = stageBlock?.errors_by_category ?? stageBlock?.errorsByCategory
    const doNotLienEngaged = Boolean(activeCaseState?.case?.do_not_lien)
    const georgiaClerkErrors = Array.isArray(stageErrorsByCategory?.georgia_clerk_errors)
        ? stageErrorsByCategory.georgia_clerk_errors
        : Array.isArray(stageErrorsByCategory?.georgiaClerkErrors)
          ? stageErrorsByCategory.georgiaClerkErrors
          : []
    const hasGeorgiaClerkErrors = georgiaClerkErrors.some(error => `${error || ''}`.trim() !== '')

    function getSideForCountyMode() {
        if (mode !== 'county') {
            return null
        }

        return documentProp === 'patient_document' ? 'patient' : 'practice'
    }

    function getSideStatus(side) {
        return getLienSideStatus(item, side)
    }

    function sideHasRetryableProblemForView(side) {
        return lienSideHasRetryableProblem(item, side)
    }

    function getRecreateTargetType() {
        if (mode === 'county') {
            return documentProp === 'patient_document' ? 'patient' : 'practice'
        }

        const patientRejected = lienSideIsRejected(item?.patient_lien_status)
        const practiceRejected = lienSideIsRejected(item?.practice_lien_status)
        if (patientRejected && !practiceRejected) {
            return 'patient'
        }
        if (practiceRejected && !patientRejected) {
            return 'practice'
        }

        const hasPracticeError = lienSideHasApiError(item?.practice_lien_status)
        const hasPatientError = lienSideHasApiError(item?.patient_lien_status)
        if (hasPracticeError && hasPatientError) {
            return 'both'
        }
        if (hasPatientError) {
            return 'patient'
        }
        if (hasPracticeError) {
            return 'practice'
        }
        const patientUnpaid = sideNeedsPaymentRetry(item, 'patient')
        const practiceUnpaid = sideNeedsPaymentRetry(item, 'practice')
        if (patientUnpaid && practiceUnpaid) {
            return 'both'
        }
        if (patientUnpaid) {
            return 'patient'
        }
        if (practiceUnpaid) {
            return 'practice'
        }

        const patientManual = sideNeedsManualRegenerate(item, 'patient')
        const practiceManual = sideNeedsManualRegenerate(item, 'practice')
        if (patientManual && practiceManual) {
            return 'both'
        }
        if (patientManual) {
            return 'patient'
        }
        if (practiceManual) {
            return 'practice'
        }

        return 'both'
    }

    const countySide = getSideForCountyMode()
    const sideStatusForView = countySide ? getSideStatus(countySide) : null
    const sideIsRejected = countySide
        ? lienSideIsRejected(sideStatusForView)
        : itemHasRejectedSide(item)

    const recreateTargetType = getRecreateTargetType()
    const needsPaymentRetryOnly = lienNeedsPaymentRetryOnly(item, recreateTargetType)
        && sidesForTargetType(recreateTargetType).some((side) => sideNeedsPaymentRetry(item, side))
        && !sidesForTargetType(recreateTargetType).some((side) => sideNeedsFullResubmit(item, side))

    const isErroredLien =
        hasGeorgiaClerkErrors ||
        (countySide
            ? sideHasRetryableProblemForView(countySide)
            : (lienSideHasApiError(item?.practice_lien_status) || lienSideHasApiError(item?.patient_lien_status) || itemHasRejectedSide(item)))

    const needsManualRegenerateOnly = !isErroredLien
        && !needsPaymentRetryOnly
        && lienNeedsManualRegenerate(item, recreateTargetType)

    const showRetryActions = shouldShowLienRetryActions({
        item,
        isErroredLien,
        needsPaymentRetryOnly,
        needsManualRegenerate: needsManualRegenerateOnly,
        recreateTargetType,
    })

    const showErrorRetryLayout = showRetryActions && (isErroredLien || needsPaymentRetryOnly)
    const showManualRegenerateBtn = showRetryActions && needsManualRegenerateOnly

    function getLienRowLabel() {
        if (item?.type === 'intent') {
            return getDisplayNameForLien(item.type)
        }

        let countyName = ''

        if (mode === 'county') {
            countyName = documentProp === 'patient_document'
                ? (item?.patient_county || '')
                : (item?.practice_county || '')
        } else {
            countyName = item?.patient_county
                || item?.practice_county
                || activeCase?.patient?.contact_information?.county
                || clinic?.contact_information?.county
                || ''
        }

        return getDisplayNameForLienWithCounty(item?.type, countyName)
    }

    const lienRowLabel = getLienRowLabel()

    function getSatisfactionSourceSide() {
        return documentProp === 'patient_document' ? 'patient' : 'practice'
    }

    const satisfactionSourceSide = item?.type === 'satisfy' ? getSatisfactionSourceSide() : null
    const satisfactionSourceBookPage = satisfactionSourceSide
        ? sourceLienBookPage(
            resolveSatisfactionSourceLienForDisplay(fullData, item, satisfactionSourceSide),
            satisfactionSourceSide
        )
        : ''

    function renderLienRowLabelContent(labelNode) {
        if (!satisfactionSourceBookPage) {
            return labelNode
        }

        return (
            <span className={classes.lien_row_label_stack}>
                {labelNode}
                <span className={classes.source_book_page}>{satisfactionSourceBookPage}</span>
            </span>
        )
    }

    function getCopyablePackageUuids() {
        if (mode === 'county') {
            const uuid = uuidProp ? item?.[uuidProp] : null
            if (!isValidGscccaPackageUuid(uuid)) {
                return []
            }

            const title = uuidProp === 'patient_package_uuid'
                ? 'Copy patient UUID'
                : 'Copy practice UUID'

            return [{ uuid: `${uuid}`.trim(), title }]
        }

        const practiceUuid = item?.practice_package_uuid
        const patientUuid = item?.patient_package_uuid
        const practiceValid = isValidGscccaPackageUuid(practiceUuid)
        const patientValid = isValidGscccaPackageUuid(patientUuid)

        if (practiceValid && patientValid) {
            const practiceTrimmed = `${practiceUuid}`.trim()
            const patientTrimmed = `${patientUuid}`.trim()
            if (practiceTrimmed.toLowerCase() === patientTrimmed.toLowerCase()) {
                return [{ uuid: practiceTrimmed, title: 'Copy clerk UUID' }]
            }

            return [
                { uuid: practiceTrimmed, title: 'Copy practice UUID' },
                { uuid: patientTrimmed, title: 'Copy patient UUID' },
            ]
        }

        if (practiceValid) {
            return [{ uuid: `${practiceUuid}`.trim(), title: 'Copy practice UUID' }]
        }

        if (patientValid) {
            return [{ uuid: `${patientUuid}`.trim(), title: 'Copy patient UUID' }]
        }

        return []
    }

    function renderPackageUuidCopyButtons() {
        const copyableUuids = getCopyablePackageUuids()
        if (copyableUuids.length === 0) {
            return null
        }

        return copyableUuids.map(({ uuid, title }) => (
            <CopyIconButton key={`${title}-${uuid}`} textToCopy={uuid} title={title} />
        ))
    }

    function getLienErrorMessage() {
        if (hasGeorgiaClerkErrors) {
            return georgiaClerkErrors
                .map(error => `${error || ''}`.trim())
                .filter(Boolean)
                .join('\n')
        }

        const stageErrors = stageBlock?.all_errors ?? stageBlock?.allErrors
        if (!stageErrors) {
            return 'Lien has an error. Please retry.'
        }

        const normalize = (value) => {
            if (Array.isArray(value)) return value.flatMap(normalize)
            if (value && typeof value === 'object') return Object.values(value).flatMap(normalize)
            if (value == null) return []
            return [`${value}`]
        }

        const messages = normalize(stageErrors)
            .map(message => message.trim())
            .filter(Boolean)

        return messages.length > 0 ? messages.join('\n') : 'Lien has an error. Please retry.'
    }

    const lienErrorMessage = getLienErrorMessage()

    async function recreateLienHandler() {
        if (doNotLienEngaged) {
            return
        }

        let actionType = 'resubmit'
        if (needsManualRegenerateOnly) {
            actionType = 'regenerate'
        } else if (needsPaymentRetryOnly && !sideIsRejected && !isErroredLien) {
            actionType = 'payment'
        }

        const confirmed = await confirmLienRetry({
            actionType,
            sideIsRejected,
            errorMessage: lienErrorMessage,
        })
        if (!confirmed) {
            return
        }

        const caseId = activeCaseState?.case?.id
        const casePracticeId = fullData?.id
        const caseLienId = item?.id

        if (!caseId || !casePracticeId || !caseLienId) {
            dispatch(notificationActions.setError({ errorMessage: 'Unable to retry lien: missing required identifiers.' }))
            return
        }

        setRecreateLoading(true)
        const payload = {
            case_practice_id: Number(casePracticeId),
            case_lien_id: Number(caseLienId),
            target_type: recreateTargetType,
        }

        const response = await recreateLien(userToken, caseId, payload, dispatch)
        if (response && response.errorMessage) {
            dispatch(notificationActions.setError(response))
        }
        setRecreateLoading(false)
    }

    /** Fallback: file a county side that failed or for legacy single-side lien rows. */
    async function fileMissingCountyLienHandler() {
        const caseId = activeCaseState?.case?.id
        const casePracticeId = fullData?.id
        const caseLienId = item?.id
        const targetType = documentProp === 'patient_document' ? 'patient' : 'practice'

        if (!caseId || !casePracticeId || !caseLienId) {
            dispatch(notificationActions.setError({ errorMessage: 'Unable to file lien: missing required identifiers.' }))
            return
        }

        setRecreateLoading(true)
        const payload = {
            case_practice_id: Number(casePracticeId),
            case_lien_id: Number(caseLienId),
            target_type: targetType,
        }
        const response = await recreateLien(userToken, caseId, payload, dispatch)
        if (response && response.errorMessage) {
            dispatch(notificationActions.setError(response))
        }
        setRecreateLoading(false)
    }

    const hasAnyPackageUuid = !!(item?.practice_package_uuid || item?.patient_package_uuid)
    const showFileMissingCountyLien =
        !doNotLienEngaged &&
        mode === 'county' &&
        !isErroredLien &&
        !hasAnyPackageUuid &&
        item?.type !== 'intent'

    const renderDocumentView = (doc) => {
        if (!lienDocumentIsAttached(doc)) {
            return null
        }

        if (!documentHasFileId(doc)) {
            return (
                <HoverWrapperInfo modalText="Document not yet uploaded to Box">
                    <Button
                        btnStyle="btn_small_icon"
                        isDisabled
                        title="Document not yet uploaded to Box"
                    >
                        <i className="ti ti-eye" />
                    </Button>
                </HoverWrapperInfo>
            )
        }

        return (
            <HoverWrapperInfo modalText="View document">
                <ViewDocument
                    userToken={userToken}
                    activeCase={activeCase}
                    item={doc}
                    btnStyle="btn_small_icon"
                    docType="document"
                    title="View document"
                >
                    <i className="ti ti-eye" />
                </ViewDocument>
            </HoverWrapperInfo>
        )
    }

    function getUploadSide() {
        if (mode === 'county') {
            return documentProp === 'patient_document' ? 'patient' : 'practice'
        }

        if (bookProp === 'patient_book' || pageProp === 'patient_page') {
            return 'patient'
        }

        return 'practice'
    }

    const renderRecordedUpload = () => {
        if (lienEditDisabled || doNotLienEngaged || item?.type === 'intent') {
            return null
        }

        if (!lienSideHasRecordedBookPage(item, bookProp, pageProp)) {
            return null
        }

        return (
            <UploadRecordedLienDocument
                caseLienId={item.id}
                side={getUploadSide()}
            />
        )
    }

    const renderFetchStatus = () => {
        if (lienEditDisabled || doNotLienEngaged || item?.type === 'intent' || showRetryActions) {
            return null
        }

        const side = getUploadSide()
        if (!sideCanFetchLienStatus(item, side)) {
            return null
        }

        return (
            <FetchLienStatus
                caseLienId={item.id}
                side={side}
            />
        )
    }

    const renderDocumentCell = (doc) => {
        const view = renderDocumentView(doc)
        const upload = renderRecordedUpload()
        const fetchStatus = renderFetchStatus()

        if (!view && !upload && !fetchStatus) {
            return null
        }

        return (
            <span className={classes.document_actions}>
                {view}
                {upload}
                {fetchStatus}
            </span>
        )
    }

    function renderManualRegenerateActions() {
        if (doNotLienEngaged) {
            return null
        }

        return (
            <Button btnStyle={'btn_small'} click={recreateLienHandler} isLoading={recreateLoading}>
                Regenerate
            </Button>
        )
    }

    function renderLienDateCell(side = null) {
        if (isTestCaseForLien(activeCase)) {
            return <DisplayBoxRight>{formatTimeLocal(item.created_at)}</DisplayBoxRight>
        }

        const displayDate = side
            ? getLienDisplayDateForSide(item, side)
            : getLienDisplayDate(item)
        if (!displayDate) {
            return <DisplayBoxRight></DisplayBoxRight>
        }

        const dateText = formatDateLocal(displayDate)
        const createdHover = lienDisplayUsesRecordingDate(item, side)
            ? formatCreatedOnHover(
                side ? getLienCreatedDateForSide(item, side) : getLienCreatedDate(item),
                formatDateLocal
            )
            : null

        return (
            <DisplayBoxRight>
                <span title={createdHover || undefined}>{dateText}</span>
            </DisplayBoxRight>
        )
    }

    function renderErrorActions() {
        const hoverMessage = sideIsRejected
            ? 'Rejected by county clerk. Retry will submit a new lien filing.'
            : needsPaymentRetryOnly && !isErroredLien
            ? 'Lien was submitted to the county clerk but payment is still pending. Retry payment to complete filing.'
            : (`${lienErrorMessage || ''}`.trim() || 'Lien has an error. Please retry.')
        const retryLabel = needsPaymentRetryOnly && !sideIsRejected ? 'Retry payment' : 'Retry'
        const badgeLabel = sideIsRejected ? 'REJECTED' : 'ERROR'

        return (
            <div className={classes.error_actions}>
                {isErroredLien ? (
                    <span className={classes.error_badge} title={hoverMessage}>{badgeLabel}</span>
                ) : null}
                {!doNotLienEngaged && (
                    <Button btnStyle={'btn_small'} click={recreateLienHandler} isLoading={recreateLoading}>
                        {retryLabel}
                    </Button>
                )}
                {renderDocumentCell(viewDoc)}
            </div>
        )
    }

    if (mode === 'county') {
        const uuidLabel =
            uuidProp === 'practice_package_uuid'
                ? 'practice uuid'
                : uuidProp === 'patient_package_uuid'
                    ? 'patient uuid'
                    : 'uuid'

        return (
            <>
                <tr className={showErrorRetryLayout ? classes.disabled_row : ''}>
                    <td>
                        <span className={classes.label_with_copy}>
                            {renderPackageUuidCopyButtons()}
                            <BodySimpleModalV3
                                message={isTestCaseForLien(activeCase) && uuidValue ? (
                                    <div>
                                        {uuidLabel} - <strong>{uuidValue}</strong>
                                    </div>
                                ) : null}
                                disabled={!isTestCaseForLien(activeCase)}>
                                {uuidValue ? (
                                    <HoverWrapperInfo modalText={uuidValue}>
                                        {renderLienRowLabelContent(lienRowLabel)}
                                    </HoverWrapperInfo>
                                ) : sideIsRejected ? (
                                    <span className={classes.error_label_with_icon}>
                                        {renderLienRowLabelContent(lienRowLabel)}
                                        <HoverWrapperInfo modalText={'Rejected'}>
                                            <i className='ti ti-alert-circle'></i>
                                        </HoverWrapperInfo>
                                    </span>
                                ) : (
                                    renderLienRowLabelContent(lienRowLabel)
                                )}
                            </BodySimpleModalV3>
                        </span>
                    </td>

                    <td className='table-cell-fit'>
                        {renderLienDateCell(getSideForCountyMode())}
                    </td>

                    {showErrorRetryLayout ? (
                        <td colSpan={3}>
                            {renderErrorActions()}
                        </td>
                    ) : (
                        <>
                            <td>
                                <LienBookPage item={item} fullData={fullData} propType={bookProp} />
                            </td>

                            <td>
                                <LienBookPage item={item} fullData={fullData} propType={pageProp} />
                            </td>

                            <td>
                                <span className={classes.document_actions}>
                                    {showManualRegenerateBtn ? renderManualRegenerateActions() : null}
                                    {renderDocumentCell(viewDoc) ?? (showFileMissingCountyLien ? (
                                        <Button btnStyle={'btn_small'} click={fileMissingCountyLienHandler} isLoading={recreateLoading}>
                                            {documentProp === 'patient_document' ? 'File patient county lien' : 'File practice county lien'}
                                        </Button>
                                    ) : (showManualRegenerateBtn ? null : '-'))}
                                </span>
                            </td>
                        </>
                    )}
                </tr>
                {lienRetryConfirmModal}
            </>
        )
    }

    return <>
        <tr className={showErrorRetryLayout ? classes.disabled_row : ''}>
            <td>
                <span className={classes.label_with_copy}>
                    {renderPackageUuidCopyButtons()}
                    <BodySimpleModalV3 message={isTestCaseForLien(activeCase) && <>
                        {item.practice_package_uuid && <div>practice uuid - <strong>{item.practice_package_uuid}</strong></div>}
                        {item.patient_package_uuid && item.patient_package_uuid != item.practice_package_uuid && <div>patient uuid - <strong>{item.patient_package_uuid}</strong></div>}
                    </>} disabled={!isTestCaseForLien(activeCase)}>
                        {renderLienRowLabelContent(lienRowLabel)}
                    </BodySimpleModalV3>
                </span>
            </td>
            <td ><CertifiedMailNumber value={item.certified_mail_number} /></td>
            <td className='table-cell-fit'>
                {renderLienDateCell(null)}
            </td>       
            {item.type == 'intent' && <td colSpan={2}>
                <CertifiedMailNumber value={item.practice_certified_mail_number} />
            </td>}
            {item.type !== 'intent' && <>
                {showErrorRetryLayout ? (
                    <td colSpan={3}>
                        {renderErrorActions()}
                    </td>
                ) : (
                    <>
                        <td>
                            <LienBookPage item={item} fullData={fullData} propType={bookProp}/>
                        </td>
                        <td>
                            <LienBookPage item={item} fullData={fullData} propType={pageProp}/>
                        </td>
                        <td>
                            <span className={classes.document_actions}>
                                {showManualRegenerateBtn ? renderManualRegenerateActions() : null}
                                {renderDocumentCell(viewDoc) ?? (showManualRegenerateBtn ? null : '-')}
                            </span>
                        </td>
                    </>
                )}
            </>}
            {item.type == 'intent' && <td>
                {renderDocumentCell(viewDoc) ?? '-'}
            </td>}
        </tr>

        {item?.type === 'intent' && getPracticeIntentNoticeDocuments(fullData).map((docItem) => (
            <tr key={docItem.id ?? docItem.name}>
                <td style={{paddingLeft: 20}}>{docItem.name}</td>
                <td ><CertifiedMailNumber value={docItem.certified_mail_number} /></td>
                <td className='table-cell-fit'>
                    {isTestCaseForLien(activeCase) ? <>
                        <DisplayBoxRight>
                            {formatTimeLocal(docItem.created_at)}
                        </DisplayBoxRight> 
                        
                    </>
                    :
                    <>
                        <DisplayBoxRight>
                            {formatDateLocal(docItem.created_at)}
                        </DisplayBoxRight>
                        
                    </>
                    }
                    {/* <DateDisplayWithNmuberOfDays date={item.created_at} hasTime={true}/> */}
                    {/* {formatDateLocalWithTime(item.created_at)}                          */}
                </td> 
                <td></td>
                <td></td>
                <td>
                    {renderDocumentView(docItem.document) ?? '-'}
                </td>
            </tr>
        ))}
        {lienRetryConfirmModal}
    </>
}